import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const JobReferralModal = ({ isOpen, onClose, job, onSuccess }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [referralData, setReferralData] = useState({
    candidateName: '',
    candidateEmail: '',
    candidatePhone: '',
    relationship: '',
    note: '',
    resumeFile: null
  });

  const relationshipOptions = [
    'Former Colleague',
    'Current Colleague',
    'Friend',
    'Family Member',
    'Professional Contact',
    'LinkedIn Connection',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReferralData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setReferralData(prev => ({
      ...prev,
      resumeFile: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('Please log in to submit a referral.');
      return;
    }

    // Validate required fields
    if (!referralData.candidateName.trim() || !referralData.candidateEmail.trim() || !referralData.relationship) {
      setError('Please fill in all required fields.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(referralData.candidateEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create referral data object
      const referral = {
        jobId: job.id,
        jobTitle: job.title,
        company: job.company,
        referrerUserId: currentUser.uid,
        referrerName: currentUser.displayName || currentUser.email,
        referrerEmail: currentUser.email,
        candidateName: referralData.candidateName.trim(),
        candidateEmail: referralData.candidateEmail.trim(),
        candidatePhone: referralData.candidatePhone.trim(),
        relationship: referralData.relationship,
        note: referralData.note.trim(),
        status: 'pending',
        submittedAt: new Date().toISOString(),
        hasResume: !!referralData.resumeFile
      };

      // In a real implementation, this would:
      // 1. Upload resume file to storage if provided
      // 2. Save referral to Firestore
      // 3. Send notification emails to relevant parties
      // 4. Track referral metrics

      console.log('Submitting job referral:', referral);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(referral);
      }

      // Auto-close after showing success message
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setReferralData({
          candidateName: '',
          candidateEmail: '',
          candidatePhone: '',
          relationship: '',
          note: '',
          resumeFile: null
        });
      }, 2000);

    } catch (error) {
      console.error('Error submitting referral:', error);
      setError('Failed to submit referral. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Refer Someone for This Job
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {job && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900">{job.title}</h4>
              <p className="text-sm text-gray-600">{job.company} • {job.location}</p>
            </div>
          )}

          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Referral Submitted!</h4>
              <p className="text-gray-600">Thank you for referring a candidate. We'll review the referral and be in touch.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="candidateName" className="block text-sm font-medium text-gray-700 mb-1">
                    Candidate Name *
                  </label>
                  <input
                    type="text"
                    id="candidateName"
                    name="candidateName"
                    value={referralData.candidateName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Full name of the candidate"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="candidateEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Candidate Email *
                  </label>
                  <input
                    type="email"
                    id="candidateEmail"
                    name="candidateEmail"
                    value={referralData.candidateEmail}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="candidate@email.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="candidatePhone" className="block text-sm font-medium text-gray-700 mb-1">
                    Candidate Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    id="candidatePhone"
                    name="candidatePhone"
                    value={referralData.candidatePhone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Relationship to Candidate *
                  </label>
                  <select
                    id="relationship"
                    name="relationship"
                    value={referralData.relationship}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    required
                  >
                    <option value="">Select relationship</option>
                    {relationshipOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="resumeFile" className="block text-sm font-medium text-gray-700 mb-1">
                    Resume (Optional)
                  </label>
                  <input
                    type="file"
                    id="resumeFile"
                    name="resumeFile"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  />
                  <p className="text-xs text-gray-500 mt-1">PDF, DOC, or DOCX files only</p>
                </div>

                <div>
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    value={referralData.note}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Why do you think this candidate would be a good fit?"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Referral'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobReferralModal;
